/**
 * Block deprecation
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-deprecation/
 * @see https://developer.wordpress.org/news/2023/03/block-deprecation-a-tutorial/
 */

import { RichText, useBlockProps } from '@wordpress/block-editor';
import { arrowRightIcon } from '../../img/icons';
import { getBlockPropAttr } from '../utils/block-utils';
import { filterOutWhiteSpace } from '../utils/general-utils';

// Old arrow icon without stroke-linecap attribute
const arrowRightIconOld = (
  <svg
    className="arrow-right"
    xmlns="http://www.w3.org/2000/svg"
    width="7.755"
    height="11.995"
    viewBox="0 0 7.755 11.995"
  >
    <path
      id="arrow"
      d="M244.609,4456.377l5.344,4.685-5.344,4.49"
      transform="translate(-243.198 -4454.966)"
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const v2 = {
  supports: {
    html: false,
    lock: false
  },
  attributes: {
    backgroundColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: '',
      type: 'string'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    width: {
      default: 'auto',
      type: 'string'
    },
    textButtonOption: {
      default: false,
      type: 'boolean'
    },
    url: {
      default: '',
      attribute: 'href',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    linkTarget: {
      default: false,
      type: 'boolean'
    },
    hasCTASlideFromCarousel: {
      default: false,
      type: 'boolean'
    },
    hasNewsletterParentBlock: {
      default: false,
      type: 'boolean'
    }
  },
  migrate( attributes ) {
    return {
      ...attributes,
      alignment: 'center'
    };
  },
  save({ attributes }) {
    const {
      backgroundColor,
      text,
      textColor,
      width,
      textButtonOption,
      url,
      linkTarget,
      hasCTASlideFromCarousel,
      hasNewsletterParentBlock
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__button'
    });

    /**
     * Handle additional logic
     */

    const isFill = getBlockPropAttr( blockProps, 'className', 'is-style-fill' );
    const isOutline = getBlockPropAttr( blockProps, 'className', 'is-style-outline' );

    const isFillIsDefaultLogic = isFill || ( !isFill && !isOutline );

    const buttonTypeClass = ( !textButtonOption && isFillIsDefaultLogic )
      ? 'btn--primary'
      : ( !textButtonOption && !isFillIsDefaultLogic ) ? 'btn--secondary' : 'is-text-button';

    const noBackgroundClass = ( !textButtonOption && !backgroundColor ) ? 'no-background-color' : '';
    const noTextClass = ( !textButtonOption && !textColor ) ? 'no-text-color' : '';

    /**
     * Handle element styles
     */

    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const borderStyles = textColor ? { border: `1px solid ${textColor}` } : undefined;

    const buttonStyles = isFillIsDefaultLogic && !textButtonOption
      ? bgColor
      : !isFillIsDefaultLogic && !textButtonOption ? borderStyles : undefined;

    const textStyle = textColor ? { color: textColor } : undefined;

    /**
     * Handle element classes
     */

    const buttonWrapperClasses = filterOutWhiteSpace( [ `button-wrapper text-black flex gap-10 items-center w-${width}` ] );

    const buttonClasses = filterOutWhiteSpace( [
      buttonTypeClass,
      noBackgroundClass,
      noTextClass,
      `w-${width}`
    ] );

    const textLogic = hasNewsletterParentBlock ? 'Sign up' : text;
    const renderInBlock = !!( ( text || hasNewsletterParentBlock ) && !hasCTASlideFromCarousel );

    return (
      renderInBlock && (
        <div { ...blockProps }>
          <div className={ buttonWrapperClasses } style={ { ...textStyle } }>

            { textLogic && url && (
            // eslint-disable-next-line react/jsx-no-target-blank
              <a className={`flex items-center no-underline ${buttonClasses}` } style={ { ...buttonStyles, ...textStyle } } href={ url || null } target={ linkTarget ? '_blank' : null } rel={ linkTarget ? 'noopener' : null }>{ textLogic }</a>
            ) }

            { textLogic && !url && (
              <RichText.Content
                className={ buttonClasses }
                tagName='button'
                value={ textLogic }
                style={ { ...buttonStyles, ...textStyle } }
              />
            ) }

            { textButtonOption ? arrowRightIconOld : undefined }
          </div>
        </div>
      )
    );
  }
};

const v1 = {
  supports: {
    html: false,
    lock: false
  },
  attributes: {
    backgroundColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: '',
      type: 'string'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    width: {
      default: 'auto',
      type: 'string'
    },
    textButtonOption: {
      default: false,
      type: 'boolean'
    },
    url: {
      default: '',
      attribute: 'href',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    linkTarget: {
      default: false,
      type: 'boolean'
    },
    hasCTASlideFromCarousel: {
      default: false,
      type: 'boolean'
    },
    hasNewsletterParentBlock: {
      default: false,
      type: 'boolean'
    }
  },
  migrate( attributes ) {
    return {
      ...attributes,
      alignment: 'center'
    };
  },
  save({ attributes }) {
    const {
      backgroundColor,
      text,
      textColor,
      width,
      textButtonOption,
      url,
      linkTarget,
      hasCTASlideFromCarousel,
      hasNewsletterParentBlock
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__button'
    });

    /**
     * Handle additional logic
     */

    const isFill = getBlockPropAttr( blockProps, 'className', 'is-style-fill' );
    const isOutline = getBlockPropAttr( blockProps, 'className', 'is-style-outline' );

    const isFillIsDefaultLogic = isFill || ( !isFill && !isOutline );

    const buttonTypeClass = ( !textButtonOption && isFillIsDefaultLogic )
      ? 'btn--primary'
      : ( !textButtonOption && !isFillIsDefaultLogic ) ? 'btn--secondary' : 'is-text-button';

    const noBackgroundClass = ( !textButtonOption && !backgroundColor ) ? 'no-background-color' : '';
    const noTextClass = ( !textButtonOption && !textColor ) ? 'no-text-color' : '';

    /**
     * Handle element styles
     */

    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const borderStyles = textColor ? { border: `1px solid ${textColor}` } : undefined;

    const buttonStyles = isFillIsDefaultLogic && !textButtonOption
      ? bgColor
      : !isFillIsDefaultLogic && !textButtonOption ? borderStyles : undefined;

    const textStyle = textColor ? { color: textColor } : undefined;

    /**
     * Handle element classes
     */

    const buttonWrapperClasses = filterOutWhiteSpace( [ `button-wrapper text-black flex gap-10 items-center w-${width}` ] );

    const buttonClasses = filterOutWhiteSpace( [
      buttonTypeClass,
      noBackgroundClass,
      noTextClass,
      `w-${width}`
    ] );

    const textLogic = hasNewsletterParentBlock ? 'Sign up' : text;
    const renderInBlock = !!( ( text || hasNewsletterParentBlock ) && !hasCTASlideFromCarousel );

    return (
      renderInBlock && (
        <div { ...blockProps }>
          <div className={ buttonWrapperClasses } style={ { ...textStyle } }>

            { textLogic && url && (
            // eslint-disable-next-line react/jsx-no-target-blank
              <a className={`flex items-center no-underline ${buttonClasses}` } style={ { ...buttonStyles, ...textStyle } } href={ url || null } target={ linkTarget ? '_blank' : null } rel={ linkTarget ? 'noopener' : null }>{ textLogic }</a>
            ) }

            { textLogic && !url && (
              <RichText.Content
                className={ buttonClasses }
                tagName='button'
                value={ textLogic }
                style={ { ...buttonStyles, ...textStyle } }
              />
            ) }

            { textButtonOption ? arrowRightIcon : undefined }
          </div>
        </div>
      )
    );
  }
};

export default [ v2, v1 ];
